import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import Axios from "./../axios";
import { newAlert } from "./alertSlice";
import { id as editSlotId } from "../containers/EditSlot/EditSlot";
//#region Get Session
export const getSession = createAsyncThunk(
  "session/getSession",
  async (payload, thunkApi) => {
    try {
      const { data } = await Axios.get(`/api/session/`);
      return data.sessionUser;
    } catch (error) {
      const currentPath = window.location.pathname;
      // Don't redirect if on email verification page or signup page
      const isPublicPage = currentPath === "/" || 
                          currentPath === "/sign-up" || 
                          currentPath === "/sign-in" ||
                          currentPath === "/reset-password" ||
                          currentPath === "/forgot-password" ||
                          currentPath.startsWith("/email-verification-pending") ||
                          currentPath.startsWith("/verify-email");
      
      if (!isPublicPage) {
        thunkApi.dispatch(newAlert({ type: "danger", message: error.message }));
      }
      // BAD CODE - but don't redirect from public pages
      if (!isPublicPage) {
        window.location.href = "/";
      }
      return Promise.reject({ error: { message: error.message } });
    }
  }
);
//#endregion

//#region LogIn
export const logIn = createAsyncThunk(
  "session/logIn",
  async (payload, thunkApi) => {
    try {
      const { data } = await Axios.post(`/api/session/login`, payload);
      if (data.isLoggedIn) {
        thunkApi.dispatch(
          newAlert({ type: "success", message: "LogIn Completed" })
        );
      } else {
        // If login failed, extract error message from response
        const errorMessage = data.error || "Email or password is incorrect";
        thunkApi.dispatch(
          newAlert({
            type: "danger",
            message: errorMessage,
          })
        );
        // Reject with error details for component handling
        return thunkApi.rejectWithValue({
          error: { message: errorMessage },
          responseData: data,
        });
      }
      return data;
    } catch (error) {
      // Extract error message from response
      const errorMessage = 
        error.response?.data?.error || 
        error.response?.data?.message || 
        error.message || 
        "Server Error";
      
      const statusCode = error.response?.status;
      
      // Never show alerts for server errors - component will handle with generic message
      // Validation errors (400/401) should be shown in form fields
      
      return thunkApi.rejectWithValue({
        error: { message: errorMessage },
        statusCode,
        responseData: error.response?.data,
      });
    }
  }
);
//#endregion

//#region SignUp
export const signUp = createAsyncThunk(
  "session/signUp",
  async (payload, thunkApi) => {
    try {
      const { data } = await Axios.post(`/api/session/signup`, payload);
      if (data.isEmailAlreadyExists) {
        thunkApi.dispatch(
          newAlert({ type: "danger", message: "Email Already Exist" })
        );
      } 
      return data;
    } catch (error) {
      // Extract error data from axios error response
      const responseData = error?.response?.data || {};
      const isEmailAlreadyExists = responseData.isEmailAlreadyExists === true;
      
      // Handle 422 (validation errors) and other errors
      const errorMessage = responseData.error?.message 
        || responseData.message 
        || error?.message 
        || "Server Error";
      
      if (isEmailAlreadyExists) {
        thunkApi.dispatch(
          newAlert({ type: "danger", message: "Email Already Exist" })
        );
      } else {
        thunkApi.dispatch(
          newAlert({ type: "danger", message: errorMessage })
        );
      }
      
      // Return error data in a format the component can use
      // When using .unwrap(), this object becomes the error
      return thunkApi.rejectWithValue({
        isEmailAlreadyExists,
        error: { message: errorMessage },
        response: error?.response,
        responseData, // Include full response data for debugging
      });
    }
  }
);
//#endregion

//#region Logout
export const logout = createAsyncThunk(
  "session/logout",
  async (payload, thunkApi) => {
    try {
      const { data } = await Axios.delete(`/api/session/logout`);
      const type = data.isLoggedOut ? "success" : "danger";
      const message = data.isLoggedOut ? "Logout" : "ServerError";
      thunkApi.dispatch(newAlert({ type: type, message: message }));
      if (data.isLoggedOut) {
        // BAD CODE
        window.location.href = "/";
      }
      return data;
    } catch (error) {
      thunkApi.dispatch(newAlert({ type: "danger", message: "Server Error" }));
      return Promise.reject({ error: { message: error.message } });
    }
  }
);
//#endregion

//#region setTimezone
export const setTimezone = createAsyncThunk(
  "session/setTimezone",
  async (payload, thunkApi) => {
    try {
      const { data } = await Axios.put(`/api/generic/change-timezone`, payload);
      if (!data.isTimezoneChanged) {
        thunkApi.dispatch(
          newAlert({ type: "danger", message: "Could Not Change Timezone" })
        );
      } else {
        thunkApi.dispatch(
          newAlert({ type: "success", message: "Timezone Changed" })
        );
      }
      return payload.timezone;
    } catch (error) {
      thunkApi.dispatch(newAlert({ type: "danger", message: "Server Error" }));
      return Promise.reject({ error: { message: error.message } });
    }
  }
);
//#endregion

//#region Verify Email
export const verifyEmail = createAsyncThunk(
  "session/verifyEmail",
  async (payload, thunkApi) => {
    try {
      const { email, vt } = payload;
      const { data } = await Axios.post(`/api/session/verify-email`, {
        email,
        vt,
      });
      
      if (data.isEmailVerified) {
        // Email was just verified - redirect to home page
        // LandingPage will call getSession() automatically, and cookie will be set by then
      } else if (data.isEmailAlreadyVerified) {
        // Set flag to redirect to email already verified page
        // We'll handle this in the reducer
        // The flag will be set via the reducer when this action is fulfilled
      } else {
        thunkApi.dispatch(
          newAlert({ type: "danger", message: data.message || "Email verification failed" })
        );
      }
      return data;
    } catch (error) {
      const errorMessage = error.response?.data?.error?.message 
        || error.response?.data?.message 
        || error.message 
        || "Email verification failed";
      thunkApi.dispatch(
        newAlert({ type: "danger", message: errorMessage })
      );
      return thunkApi.rejectWithValue({ error: { message: errorMessage } });
    }
  }
);
//#endregion

export const sessionSlice = createSlice({
  name: "session",
  initialState: {
    activeModal: null,
    activeSpinner: {},
    sessionUser: null,
    signUp: {
      isEmailAlreadyExists: false,
    },
    pendingUser: null,
    goTo: "",
    // timezone: "Asia/Calcutta",
    selectedCandidate: null,
    isLoading: true,
    fRedirectUserToEmailAlreadyVerifiedPage: false,
    fRedirectToHomeAfterVerification: false,
    error: null,
  },
  reducers: {
    //#region Show Modal
    /**
     * payload {
     *    id: "MODAL_ID",
     *    data: { SLOT }
     * }
     */
    showModal: (state, { payload }) => {
      if (payload === null) {
        state.activeModal = null;
        //#region make search selections Empty
        state.selectedTask = [];
        state.selectedProject = [];
        state.selectedClient = [];
        //#endregion
      } else {
        state.activeModal = { id: payload.id, data: payload.data };
        if (payload.data && payload.id === editSlotId) {
          state.selectedTask = [payload.data.task];
          state.selectedProject = [payload.data.task.project];
          state.selectedClient = [payload.data.task.project.supervisingClient];
        }
      }
    },
    //#region Show Spinner
    /**
     * payload {
     *    id: "BUTTON_ID",
     * }
     */
    showSpinner: (state, { payload }) => {
      if (payload === null) {
        state.activeSpinner = null;
      } else {
        state.activeSpinner = { id: payload.id };
      }
    },
    //#endregion
    //#region Selected Client
    setSelectedClient: (state, { payload }) => {
      state.selectedClient = payload;
      state.selectedProject = [];
    },
    //#endregion
    //#region Selected Client
    setSelectedProject: (state, { payload }) => {
      state.selectedProject = payload;
      if (payload[0] !== undefined) {
        state.selectedClient = [payload[0].supervisingClient];
      }
    },
    //#endregion
    //#region Selected Task
    setSelectedTask: (state, { payload }) => {
      state.selectedTask = payload;
      if (payload[0] !== undefined && payload[0].project !== undefined) {
        state.selectedProject = [payload[0].project];
        state.selectedClient = [payload[0].project.supervisingClient];
      }
    },
    //#endregion
    //#region From and To
    addFromDate: (state, action) => {
      console.log("fromDate", action.payload)
      state.fromDate = action.payload;
    },
    addToDate: (state, action) => {
      state.toDate = action.payload;
    },
    //#endregion
    setFromRawDate: (state, action) => {
      state.fromRaw = action.payload;
    },
    setToRawDate: (state, action) => {
      state.toRaw = action.payload;
    },
    setFromTimestamp: (state, action) => {
      state.fromTimestamp = action.payload;

    },
    setToTimestamp: (state, action) => {
      state.toTimestamp = action.payload;
    },
    //#region goTo
    goTo: (state, action) => {
      state.goTo = action.payload.url;
    },
    //#endregion
    //#region Set Redirect Flag for Email Already Verified
    setFRedirectUserToEmailAlreadyVerifiedEmailPage: (state, action) => {
      state.fRedirectUserToEmailAlreadyVerifiedPage = action.payload;
    },
    //#endregion
    //#region Set Redirect Flag for Home After Verification
    setFRedirectToHomeAfterVerification: (state, action) => {
      state.fRedirectToHomeAfterVerification = action.payload;
    },
    //#endregion
    //#region Select Candidate
    selectCandidate: (state, { payload }) => {
      state.selectedCandidate = payload;
    },
    //#endregion
  },
  extraReducers: {
    //#region Get Session
    [getSession.fulfilled]: (state, { payload }) => {
      if (payload.timezone === undefined) {
        payload.timezone = "Asia/Calcutta";
      }
      state.sessionUser = payload;
      state.isLoading = false;
      // Clear pending user when session is successfully retrieved (user is verified)
      if (payload && payload.roles && payload.roles.length > 0) {
        state.pendingUser = null;
      }
    },
    [getSession.rejected]: (state) => {
      state.isLoading = false;
    },
    [getSession.pending]: (state) => {
      state.isLoading = true;
    },
    //#endregion

    //#region LogIn
    [logIn.fulfilled]: (state, { payload }) => {
      const { isLoggedIn } = payload;
      if (isLoggedIn) {
        if (payload.sessionUser.timezone === undefined) {
          payload.sessionUser.timezone = "Asia/Calcutta";
        }
        state.sessionUser = payload.sessionUser;
        state.activeModal = null;
        // Clear pending user when user successfully logs in
        state.pendingUser = null;
        // Clear any previous errors on successful login
        state.error = null;
      }
      state.activeSpinner = null;
    },
    [logIn.rejected]: (state, { payload }) => {
      state.activeSpinner = null;
      // Store error message for component access
      if (payload?.error?.message) {
        state.error = payload.error.message;
      }
    },
    [logIn.pending]: (state) => {
      // Clear previous errors when starting new login attempt
      state.error = null;
    },
    //#endregion

    //#region Sign Up
    [signUp.fulfilled]: (state, { payload }) => {
      const { isEmailAlreadyExists, pendingUser } = payload;
      if (!isEmailAlreadyExists) {
        state.activeModal = null;
        // Store pending user info for email verification page
        if (pendingUser) {
          state.pendingUser = pendingUser;
        }
      }
      state.signUp.isEmailAlreadyExists = isEmailAlreadyExists;
    },
    [signUp.rejected]: (state) => {
      state.activeSpinner = null;
    },
    [signUp.pending]: () => { },
    //#endregion

    //#region logOut
    [logout.fulfilled]: (state, { payload }) => {
      const { isLoggedOut } = payload;
      if (isLoggedOut) {
        state.sessionUser = null;
        state.goTo = "/";
      }
    },
    [logout.rejected]: () => { },
    [logout.pending]: () => { },
    //#endregion

    //#region logOut
    [setTimezone.fulfilled]: (state, { payload }) => {
      state.sessionUser.timezone = payload;
    },
    [setTimezone.rejected]: () => { },
    [setTimezone.pending]: () => { },
    //#endregion

    //#region Verify Email
    [verifyEmail.fulfilled]: (state, { payload }) => {
      if (payload && payload.isEmailVerified) {
        // Clear pending user when email is verified
        state.pendingUser = null;
        // Set flag to redirect to home page
        state.fRedirectToHomeAfterVerification = true;
      } else if (payload && payload.isEmailAlreadyVerified) {
        // Set flag to redirect to email already verified page
        state.fRedirectUserToEmailAlreadyVerifiedPage = true;
      }
      state.error = null;
    },
    [verifyEmail.rejected]: (state, { payload }) => {
      state.error = payload?.error || { message: "Email verification failed" };
    },
    [verifyEmail.pending]: (state) => {
      state.error = null;
    },
    //#endregion
  },
});

//#region Actions Export
export const {
  showModal,
  showSpinner,
  setSelectedClient,
  setSelectedProject,
  setSelectedTask,
  addFromDate,
  addToDate,
  selectCandidate,
  setFromRawDate,
  setToRawDate,
  setFromTimestamp,
  setToTimestamp,
  setFRedirectUserToEmailAlreadyVerifiedEmailPage,
  setFRedirectToHomeAfterVerification,
} = sessionSlice.actions;

export const selectTimezone = (state) => state.session.timezone;

export default sessionSlice.reducer;
